import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { ButtonNormal } from '../../../components/buttons/button-normal'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import PersonalitySelector from '../../../components/personality-selector'
import { NombreTarotista } from '../../../data/personality'
import { GetConsult } from '../../../store/actions'
import { copyResponse, fotoCartas, isEmpty } from '../../../utils/functions'
import { buildNatalPremiumPrompt, NatalChartResponse } from './functions'

const zodiacSigns = [
    'Aries',
    'Tauro',
    'Geminis',
    'Cancer',
    'Leo',
    'Virgo',
    'Libra',
    'Escorpio',
    'Sagitario',
    'Capricornio',
    'Acuario',
    'Piscis',
]

const zodiacSymbols = ['\u2648', '\u2649', '\u264A', '\u264B', '\u264C', '\u264D', '\u264E', '\u264F', '\u2650', '\u2651', '\u2652', '\u2653']

const PDF_FIRMA_ESTUDIO = 'Manantial de Luz'
const PDF_FIRMA_AUTOR = 'Marcelo Spagnuolo'

/** Firma del documento (PDF): estudio en negrita, autor en cursiva. */
const drawPdfFirma = (pdf: jsPDF, pageWidth: number, pageHeight: number, variant: 'portada' | 'cierre') => {
    const cx = pageWidth / 2
    if (variant === 'cierre') {
        const midY = pageHeight / 2 - 6
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(16)
        pdf.setTextColor(76, 29, 149)
        pdf.text(PDF_FIRMA_ESTUDIO, cx, midY, { align: 'center' })
        pdf.setFont('helvetica', 'italic')
        pdf.setFontSize(13)
        pdf.setTextColor(55, 65, 81)
        pdf.text(PDF_FIRMA_AUTOR, cx, midY + 10, { align: 'center' })
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(9)
        pdf.setTextColor(100, 116, 139)
        pdf.text('Tarot Express — Carta astral premium', cx, midY + 22, { align: 'center' })
        return
    }
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(11)
    pdf.setTextColor(76, 29, 149)
    pdf.text(PDF_FIRMA_ESTUDIO, cx, pageHeight - 22, { align: 'center' })
    pdf.setFont('helvetica', 'italic')
    pdf.setFontSize(10)
    pdf.setTextColor(55, 65, 81)
    pdf.text(PDF_FIRMA_AUTOR, cx, pageHeight - 15, { align: 'center' })
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8)
    pdf.setTextColor(120, 128, 140)
    pdf.text('Tarot Express — Carta astral premium', cx, pageHeight - 8, { align: 'center' })
}

const getAngleDiff = (a: number, b: number) => {
    const diff = Math.abs(a - b) % 360
    return diff > 180 ? 360 - diff : diff
}

const detectAspect = (a: number, b: number) => {
    const diff = getAngleDiff(a, b)
    const orb = 6
    if (Math.abs(diff - 0) <= orb) return { name: 'conjuncion', color: '#8b5cf6' }
    if (Math.abs(diff - 60) <= orb) return { name: 'sextil', color: '#2563eb' }
    if (Math.abs(diff - 90) <= orb) return { name: 'cuadratura', color: '#dc2626' }
    if (Math.abs(diff - 120) <= orb) return { name: 'trigono', color: '#16a34a' }
    if (Math.abs(diff - 180) <= orb) return { name: 'oposicion', color: '#ea580c' }
    return null
}

const NatalWheel = ({ chart }: { chart: NatalChartResponse }) => {
    const size = 620
    const center = size / 2
    const radius = 250
    const innerRadius = 180

    const planets = useMemo(() => {
        return chart.positions.map((planet) => {
            const angle = ((planet.longitude - 90) * Math.PI) / 180
            const x = center + Math.cos(angle) * ((radius + innerRadius) / 2)
            const y = center + Math.sin(angle) * ((radius + innerRadius) / 2)
            const lineX = center + Math.cos(angle) * (innerRadius - 10)
            const lineY = center + Math.sin(angle) * (innerRadius - 10)
            const textX = center + Math.cos(angle) * (radius - 40)
            const textY = center + Math.sin(angle) * (radius - 40)
            return { ...planet, x, y, lineX, lineY, textX, textY }
        })
    }, [chart, center, radius, innerRadius])

    const aspects = useMemo(() => {
        const pairs: Array<{
            from: (typeof planets)[number]
            to: (typeof planets)[number]
            color: string
            name: string
        }> = []
        for (let i = 0; i < planets.length; i++) {
            for (let j = i + 1; j < planets.length; j++) {
                const aspect = detectAspect(planets[i].longitude, planets[j].longitude)
                if (aspect) {
                    pairs.push({
                        from: planets[i],
                        to: planets[j],
                        color: aspect.color,
                        name: aspect.name,
                    })
                }
            }
        }
        return pairs
    }, [planets])

    return (
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full bg-white rounded-xl">
            <circle cx={center} cy={center} r={radius} fill="#f8fafc" stroke="#334155" strokeWidth="2" />
            <circle cx={center} cy={center} r={innerRadius} fill="#ffffff" stroke="#94a3b8" strokeWidth="2" />
            {Array.from({ length: 12 }).map((_, index) => {
                const angle = ((index * 30 - 90) * Math.PI) / 180
                const x1 = center + Math.cos(angle) * innerRadius
                const y1 = center + Math.sin(angle) * innerRadius
                const x2 = center + Math.cos(angle) * radius
                const y2 = center + Math.sin(angle) * radius
                const tx = center + Math.cos(angle + Math.PI / 12) * (radius - 18)
                const ty = center + Math.sin(angle + Math.PI / 12) * (radius - 18)
                const sx = center + Math.cos(angle + Math.PI / 12) * (innerRadius - 42)
                const sy = center + Math.sin(angle + Math.PI / 12) * (innerRadius - 42)
                return (
                    <g key={index}>
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#cbd5e1" strokeWidth="1.3" />
                        <text
                            x={tx}
                            y={ty}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="12"
                            fill="#1e293b"
                            fontWeight="700"
                        >
                            {zodiacSigns[index]}
                        </text>
                        <text
                            x={sx}
                            y={sy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="32"
                            fill="#1e293b"
                            opacity="0.24"
                        >
                            {zodiacSymbols[index]}
                        </text>
                    </g>
                )
            })}
            {aspects.map((aspect, index) => (
                <line
                    key={`${aspect.from.key}-${aspect.to.key}-${index}`}
                    x1={aspect.from.lineX}
                    y1={aspect.from.lineY}
                    x2={aspect.to.lineX}
                    y2={aspect.to.lineY}
                    stroke={aspect.color}
                    strokeWidth="1.6"
                    opacity="0.9"
                />
            ))}
            <g>
                <rect x={24} y={24} width={180} height={90} rx={8} fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
                <text x={34} y={40} fontSize="11" fill="#111827" fontWeight="700">
                    Aspectos
                </text>
                <line x1={34} y1={52} x2={54} y2={52} stroke="#8b5cf6" strokeWidth="1.8" />
                <text x={60} y={55} fontSize="10" fill="#374151">
                    Conjuncion
                </text>
                <line x1={34} y1={64} x2={54} y2={64} stroke="#2563eb" strokeWidth="1.8" />
                <text x={60} y={67} fontSize="10" fill="#374151">
                    Sextil
                </text>
                <line x1={34} y1={76} x2={54} y2={76} stroke="#dc2626" strokeWidth="1.8" />
                <text x={60} y={79} fontSize="10" fill="#374151">
                    Cuadratura
                </text>
                <line x1={118} y1={64} x2={138} y2={64} stroke="#16a34a" strokeWidth="1.8" />
                <text x={144} y={67} fontSize="10" fill="#374151">
                    Trigono
                </text>
                <line x1={118} y1={76} x2={138} y2={76} stroke="#ea580c" strokeWidth="1.8" />
                <text x={144} y={79} fontSize="10" fill="#374151">
                    Oposicion
                </text>
            </g>
            <text x={center} y={center - 10} textAnchor="middle" fontSize="20" fill="#0f172a" fontWeight="700">
                Carta Natal
            </text>
            <text x={center} y={center + 18} textAnchor="middle" fontSize="13" fill="#334155">
                {chart.fullName}
            </text>
            {planets.map((planet) => (
                <g key={planet.key}>
                    <circle cx={planet.lineX} cy={planet.lineY} r="4.5" fill="#0f172a" />
                    <line x1={planet.lineX} y1={planet.lineY} x2={planet.x} y2={planet.y} stroke="#64748b" strokeWidth="1" />
                    <text
                        x={planet.textX}
                        y={planet.textY - 4}
                        textAnchor="middle"
                        fontSize="11"
                        fill="#1d4ed8"
                        fontWeight="700"
                    >
                        {planet.name}
                    </text>
                    <text
                        x={planet.textX}
                        y={planet.textY + 10}
                        textAnchor="middle"
                        fontSize="10"
                        fill="#1d4ed8"
                        fontWeight="700"
                    >
                        {planet.degreeText}
                    </text>
                </g>
            ))}
        </svg>
    )
}

const CartaNatal = () => {
    const navigate = useNavigate()
    const dispatch: Dispatch<any> = useDispatch()
    const { user, consult, loading } = useSelector((state: any) => state)

    const [fullName, setFullName] = useState('')
    const [birthDate, setBirthDate] = useState('')
    const [birthTime, setBirthTime] = useState('')
    const [birthPlace, setBirthPlace] = useState('')
    const [chart, setChart] = useState<NatalChartResponse | null>(null)
    const [response, setResponse] = useState('')
    const [showResponse, setShowResponse] = useState(false)
    const [isGenerating, setIsGenerating] = useState(false)
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState('')
    const [personalidad, setPersonalidad] = useState<NombreTarotista>()
    /** Overlay de espera: informe IA o PDF (html2canvas + jsPDF tarda sin feedback visual). */
    const [waitOverlay, setWaitOverlay] = useState<{ title: string; subtitle: string } | null>(null)

    const canGenerate = fullName.length > 3 && birthDate.length > 0 && birthTime.length > 0 && birthPlace.length > 2
    const hasChart = Boolean(chart)
    const hasInterpretation = response.trim().length > 0

    const requestChart = async () => {
        const res = await fetch('/api/natal-chart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fullName, birthDate, birthTime, birthPlace }),
        })
        const data = await res.json()
        if (!res.ok) {
            throw new Error(data.error || 'No se pudo generar la carta natal')
        }
        return data as NatalChartResponse
    }

    /** Cálculo astral (/api/natal-chart) + informe premium en un solo paso (tipo carta-natal-premium en backend). */
    const generarCartaNatal = async () => {
        if (!personalidad) return
        try {
            setIsGenerating(true)
            setResponse('')
            setShowResponse(false)
            setWaitOverlay({
                title: 'Carta natal',
                subtitle: 'Calculando posiciones en el servidor (geocodificación y efemérides)…',
            })
            let chartData = chart
            if (!chartData) {
                chartData = await requestChart()
                setChart(chartData)
            }

            setWaitOverlay({
                title: 'Informe premium',
                subtitle:
                    'La IA está redactando un informe largo (varios minutos). No cierres la pestaña. Abajo se muestra el avance en caracteres mientras llega el stream.',
            })

            const info = {
                consult: buildNatalPremiumPrompt(chartData),
                user: user.username,
                personality: personalidad,
                clientName: chartData.fullName,
                clientAnswer: '',
                cards: [],
                type: 'carta-natal-premium',
            }
            const fullText = (await dispatch(GetConsult(info))) as unknown
            if (typeof fullText === 'string' && fullText.trim().length > 0) {
                setResponse(fullText)
                Swal.fire('Informe listo', 'Podés ver la interpretación, copiar o generar el PDF.', 'success')
            } else {
                Swal.fire('Atención', 'No se recibió texto del informe. Probá de nuevo.', 'warning')
            }
        } catch (error: any) {
            Swal.fire('Error', error.message || 'No se pudo generar la carta natal', 'error')
        } finally {
            setIsGenerating(false)
            setWaitOverlay(null)
        }
    }

    const copiarImagen = () => {
        const element = document.getElementById('natal-wheel-capture')
        if (!element) return
        fotoCartas(element, `carta-natal-${fullName.replaceAll(' ', '-').toLowerCase()}`)
    }

    const createPremiumPdf = async (
        onProgress?: (message: string) => void
    ): Promise<{ pdf: jsPDF; fileName: string }> => {
        if (!chart || !response.trim()) {
            throw new Error('Primero generá la carta y esperá a que termine el informe')
        }
        const element = document.getElementById('natal-wheel-capture')
        if (!element) {
            throw new Error('No se encontro la imagen de la carta')
        }

        onProgress?.('Capturando la rueda natal en imagen (alta resolución)…')
        const canvas = await html2canvas(element, {
            allowTaint: true,
            useCORS: true,
            scale: 2,
            backgroundColor: '#ffffff',
        })
        const imageData = canvas.toDataURL('image/png')

        onProgress?.('Armando el PDF: portada, tablas técnicas e interpretación (puede tardar un minuto)…')

        const pdf = new jsPDF('p', 'mm', 'a4')
        const pageWidth = 210
        const pageHeight = 297
        const margin = 14
        let y = margin
        const contentWidth = pageWidth - margin * 2

        const ensureSpace = (needed = 8) => {
            if (y + needed > pageHeight - margin) {
                pdf.addPage()
                y = margin
            }
        }

        const sectionTitle = (title: string) => {
            ensureSpace(12)
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(13)
            pdf.setTextColor(46, 16, 101)
            const titleLines = pdf.splitTextToSize(title, contentWidth)
            for (const line of titleLines) {
                ensureSpace(6)
                pdf.text(line, margin, y)
                y += 5
            }
            pdf.setDrawColor(196, 181, 253)
            pdf.line(margin, y, pageWidth - margin, y)
            y += 5
            pdf.setTextColor(17, 24, 39)
        }

        const drawTable = (headers: string[], rows: string[][]) => {
            const colCount = headers.length
            const tableWidth = contentWidth
            const colWidth = tableWidth / colCount
            const rowHeight = 6

            ensureSpace(14)
            pdf.setFillColor(237, 233, 254)
            pdf.rect(margin, y, tableWidth, rowHeight, 'F')
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(9)
            pdf.setTextColor(46, 16, 101)
            headers.forEach((header, idx) => {
                pdf.text(header, margin + idx * colWidth + 1.5, y + 4.2)
            })
            y += rowHeight

            pdf.setFont('helvetica', 'normal')
            pdf.setTextColor(17, 24, 39)
            rows.forEach((row, rowIndex) => {
                ensureSpace(rowHeight + 1)
                if (rowIndex % 2 === 0) {
                    pdf.setFillColor(250, 250, 252)
                    pdf.rect(margin, y, tableWidth, rowHeight, 'F')
                }
                row.forEach((cell, idx) => {
                    const maxChars = Math.max(10, Math.floor(colWidth / 1.9))
                    const text = cell.length > maxChars ? `${cell.slice(0, maxChars - 2)}...` : cell
                    pdf.text(text, margin + idx * colWidth + 1.5, y + 4.2)
                })
                y += rowHeight
            })

            pdf.setDrawColor(203, 213, 225)
            for (let i = 0; i <= colCount; i++) {
                const x = margin + i * colWidth
                pdf.line(x, y - rowHeight * (rows.length + 1), x, y)
            }
            pdf.line(margin, y, margin + tableWidth, y)
        }

        const bodyText = (text: string, size = 10, lineHeight = 5) => {
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(size)
            const lines = pdf.splitTextToSize(text, contentWidth)
            for (const line of lines) {
                ensureSpace(lineHeight + 1)
                pdf.text(line, margin, y)
                y += lineHeight
            }
        }

        const parsePremiumSections = (raw: string) => {
            const blocks = raw
                .split(/\n(?=\d+\)\s)/g)
                .map((block) => block.trim())
                .filter(Boolean)
            if (blocks.length === 0) {
                return [{ title: 'Informe', content: raw.trim() }]
            }
            return blocks.map((block) => {
                const lines = block.split('\n')
                const first = lines[0] || ''
                const content = lines.slice(1).join('\n').trim()
                const title = first.replace(/^\d+\)\s*/, '').trim() || 'Seccion'
                return { title, content: content || first }
            })
        }

        // Cover
        pdf.setFillColor(245, 243, 255)
        pdf.rect(0, 0, pageWidth, pageHeight, 'F')
        pdf.setFont('helvetica', 'bold')
        pdf.setTextColor(46, 16, 101)
        pdf.setFontSize(24)
        pdf.text('Carta Astral Premium', margin, 38)
        pdf.setFontSize(12)
        pdf.setTextColor(55, 65, 81)
        pdf.text('Informe personalizado de astrologia natal', margin, 46)
        pdf.setDrawColor(167, 139, 250)
        pdf.line(margin, 52, pageWidth - margin, 52)

        pdf.setTextColor(31, 41, 55)
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(11)
        pdf.text(`Consultante: ${chart.fullName}`, margin, 66)
        pdf.text(`Nacimiento: ${chart.birthDate} ${chart.birthTime}`, margin, 73)
        pdf.text(`Lugar: ${chart.birthPlace}`, margin, 80)
        pdf.text(`Zona horaria: ${chart.timezone}`, margin, 87)
        pdf.text(`Generado: ${new Date().toLocaleString('es-AR')}`, margin, 94)

        const coverImgWidth = 118
        const coverImgHeight = 118
        const coverImgX = pageWidth - margin - coverImgWidth
        const coverImgY = 108
        pdf.addImage(imageData, 'PNG', coverImgX, coverImgY, coverImgWidth, coverImgHeight)

        drawPdfFirma(pdf, pageWidth, pageHeight, 'portada')

        // Index page
        pdf.addPage()
        y = margin
        sectionTitle('Indice del informe')
        bodyText('1. Resumen tecnico', 10, 5)
        bodyText('2. Aspectos destacados', 10, 5)
        bodyText('3. Interpretacion integral premium', 10, 5)
        y += 3
        sectionTitle('Resumen natal rapido')
        bodyText(`Sol en ${chart.summary.sunSign} | Luna en ${chart.summary.moonSign}`)
        y += 3

        // Technical page
        pdf.addPage()
        y = margin
        sectionTitle('Resumen tecnico')

        const elementSummary = Object.entries(chart.technical.distributions.byElement)
            .map(([k, v]) => `${k}: ${v}`)
            .join(' | ')
        const modalitySummary = Object.entries(chart.technical.distributions.byModality)
            .map(([k, v]) => `${k}: ${v}`)
            .join(' | ')
        const polaritySummary = Object.entries(chart.technical.distributions.byPolarity)
            .map(([k, v]) => `${k}: ${v}`)
            .join(' | ')

        bodyText(`Elementos: ${elementSummary}`)
        bodyText(`Modalidades: ${modalitySummary}`)
        bodyText(`Polaridad: ${polaritySummary}`)
        y += 2

        sectionTitle('Tabla de posiciones planetarias')
        drawTable(
            ['Planeta', 'Signo', 'Grado', 'Longitud'],
            chart.positions.map((position) => [
                position.name,
                position.sign,
                position.degreeText,
                `${position.longitude.toFixed(2)}°`,
            ])
        )
        y += 4

        sectionTitle('Distribucion por elementos')
        drawTable(
            ['Elemento', 'Cantidad'],
            Object.entries(chart.technical.distributions.byElement).map(([key, value]) => [key, String(value)])
        )
        y += 4

        sectionTitle('Distribucion por modalidades')
        drawTable(
            ['Modalidad', 'Cantidad'],
            Object.entries(chart.technical.distributions.byModality).map(([key, value]) => [key, String(value)])
        )
        y += 4

        sectionTitle('Distribucion por polaridad')
        drawTable(
            ['Polaridad', 'Cantidad'],
            Object.entries(chart.technical.distributions.byPolarity).map(([key, value]) => [key, String(value)])
        )
        y += 4

        const aspectLines = chart.technical.aspects.slice(0, 10).map((a) => `${a.from} ${a.type} ${a.to}`)
        sectionTitle('Aspectos destacados (con simbologia)')
        if (aspectLines.length > 0) {
            for (const line of aspectLines) {
                bodyText(`- ${line}`, 10, 4.5)
            }
        } else {
            bodyText('No se detectaron aspectos mayores dentro del orbe configurado.')
        }

        if (chart.technical.aspects.length > 0) {
            y += 3
            sectionTitle('Tabla de aspectos')
            drawTable(
                ['Planeta A', 'Aspecto', 'Planeta B', 'Angulo'],
                chart.technical.aspects.map((aspect) => [
                    aspect.from,
                    aspect.type,
                    aspect.to,
                    `${aspect.angle}°`,
                ])
            )
        }

        // Interpretation pages
        const sections = parsePremiumSections(response)
        pdf.addPage()
        y = margin
        sectionTitle('Interpretacion integral premium')
        for (const section of sections) {
            sectionTitle(section.title)
            bodyText(section.content || 'Sin contenido para esta seccion.')
            y += 2
        }

        pdf.addPage()
        pdf.setFillColor(250, 245, 255)
        pdf.rect(0, 0, pageWidth, pageHeight, 'F')
        drawPdfFirma(pdf, pageWidth, pageHeight, 'cierre')

        const fileName = `carta-astral-premium-${chart.fullName.replaceAll(' ', '-').toLowerCase()}.pdf`
        return { pdf, fileName }
    }

    const previewPdfPremium = async () => {
        if (!chart || !response.trim()) return
        try {
            setIsDownloadingPdf(true)
            setWaitOverlay({ title: 'Generando PDF', subtitle: 'Preparando…' })
            const { pdf } = await createPremiumPdf((msg) => setWaitOverlay({ title: 'Generando PDF', subtitle: msg }))
            if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl)
            const blob = pdf.output('blob')
            const url = URL.createObjectURL(blob)
            setPdfPreviewUrl(url)
            window.open(url, '_blank', 'noopener,noreferrer')
        } catch (error: any) {
            Swal.fire('Error', error.message || 'No se pudo generar la vista previa del PDF', 'error')
        } finally {
            setIsDownloadingPdf(false)
            setWaitOverlay(null)
        }
    }

    const descargarPdfPremium = async () => {
        if (!chart || !response.trim()) return
        try {
            setIsDownloadingPdf(true)
            setWaitOverlay({ title: 'Generando PDF', subtitle: 'Preparando…' })
            const { pdf, fileName } = await createPremiumPdf((msg) =>
                setWaitOverlay({ title: 'Generando PDF', subtitle: msg })
            )
            pdf.save(fileName)
        } catch (error: any) {
            Swal.fire('Error', error.message || 'No se pudo generar el PDF premium', 'error')
        } finally {
            setIsDownloadingPdf(false)
            setWaitOverlay(null)
        }
    }

    const resetear = () => {
        setFullName('')
        setBirthDate('')
        setBirthTime('')
        setBirthPlace('')
        setChart(null)
        setResponse('')
        setShowResponse(false)
        if (pdfPreviewUrl) {
            URL.revokeObjectURL(pdfPreviewUrl)
            setPdfPreviewUrl('')
        }
    }

    const handlePersonalitySelector = (id: NombreTarotista) => {
        localStorage.setItem('personality', id)
        setPersonalidad(id)
    }

    useEffect(() => {
        const saved = localStorage.getItem('personality')
        if (Object.entries(user).length === 0 || isEmpty(saved)) {
            navigate('/')
            return
        }
        if (user.level !== 1) {
            navigate('/')
            return
        }
        setPersonalidad(saved as NombreTarotista)
    }, [navigate, user])

    useEffect(() => {
        if (!isEmpty(consult) && consult.type === 'carta-natal-premium') {
            setResponse(consult.response || '')
        }
    }, [consult])

    return (
        <div className="relative w-full bg-natal bg-cover bg-no-repeat px-4 mini:min-h-screen mini:h-full mini:pb-10 xl:h-height-main xl:pb-0">
            <p className="w-full py-5 italic font-bold text-center text-white mini:text-xl xl:text-4xl">Carta Natal</p>
            <div className="flex w-full gap-4 mini:flex-col xl:flex-row">
                <div className="flex flex-col gap-4 rounded-lg mini:w-full xl:w-1/2 bg-violetLight/70 p-4 shadow-card-horizontal">
                    <input
                        type="text"
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        placeholder="Nombre completo"
                        className="h-10 px-3 text-base text-gray-600 rounded-lg focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2"
                        readOnly={hasChart}
                    />
                    <div className="flex gap-3 mini:flex-col md:flex-row">
                        <input
                            type="date"
                            value={birthDate}
                            onChange={(event) => setBirthDate(event.target.value)}
                            className="h-10 px-3 text-base text-gray-600 rounded-lg mini:w-full md:w-1/2"
                            disabled={hasChart}
                        />
                        <input
                            type="time"
                            value={birthTime}
                            onChange={(event) => setBirthTime(event.target.value)}
                            className="h-10 px-3 text-base text-gray-600 rounded-lg mini:w-full md:w-1/2"
                            disabled={hasChart}
                        />
                    </div>
                    <input
                        type="text"
                        value={birthPlace}
                        onChange={(event) => setBirthPlace(event.target.value)}
                        placeholder="Lugar de nacimiento (ciudad, pais)"
                        className="h-10 px-3 text-base text-gray-600 rounded-lg focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2"
                        readOnly={hasChart}
                    />
                    <div className="flex flex-wrap gap-2">
                        {!hasChart && canGenerate ? (
                            <ButtonNormal
                                caption={isGenerating ? 'Generando carta e informe...' : 'Generar carta natal'}
                                onClick={generarCartaNatal}
                                disabled={!personalidad || isGenerating}
                                loading={isGenerating}
                            />
                        ) : null}
                        <ButtonNormal
                            caption="Copiar imagen"
                            onClick={copiarImagen}
                            disabled={!chart || loading || isGenerating}
                        />
                        {hasInterpretation ? (
                            <>
                                <ButtonNormal
                                    caption="Ver interpretación"
                                    onClick={() => setShowResponse(true)}
                                    disabled={loading || isGenerating}
                                />
                                <ButtonNormal
                                    caption={isDownloadingPdf ? 'Preparando vista PDF...' : 'Ver PDF'}
                                    onClick={previewPdfPremium}
                                    disabled={isGenerating || isDownloadingPdf}
                                    loading={isDownloadingPdf}
                                />
                                <ButtonNormal
                                    caption={isDownloadingPdf ? 'Generando PDF...' : 'Descargar PDF'}
                                    onClick={descargarPdfPremium}
                                    disabled={isGenerating || isDownloadingPdf}
                                    loading={isDownloadingPdf}
                                />
                            </>
                        ) : null}
                        <ButtonNormal
                            caption="Nueva carta"
                            onClick={resetear}
                            disabled={(!chart && response.length === 0) || isGenerating || isDownloadingPdf}
                        />
                    </div>
                    {chart ? (
                        <div className="p-3 text-sm bg-white rounded-lg text-violet-900">
                            <p>
                                <strong>Sol:</strong> {chart.summary.sunSign} | <strong>Luna:</strong>{' '}
                                {chart.summary.moonSign}
                            </p>
                            <p>
                                <strong>Lugar resuelto:</strong> {chart.birthPlace}
                            </p>
                            <p>
                                <strong>Zona horaria:</strong> {chart.timezone}
                            </p>
                        </div>
                    ) : null}
                </div>
                {chart ? (
                    <div className="mini:w-full xl:w-1/2">
                        <div id="natal-wheel-capture" className="w-full p-3 bg-white rounded-xl shadow-card-horizontal">
                            <NatalWheel chart={chart} />
                        </div>
                    </div>
                ) : null}
            </div>
            {showResponse ? (
                <div className="absolute w-[90vw] h-[85vh] top-[5%] left-[5%] bg-zinc-200 shadow-2xl flex flex-row gap-5 rounded-xl p-4">
                    <div className="flex flex-col w-full gap-3">
                        <textarea
                            className="w-full h-full p-3 rounded-lg resize-none"
                            value={response}
                            onChange={(event) => setResponse(event.target.value)}
                        />
                        <div className="flex flex-wrap gap-2">
                            <ButtonNormal caption="Cerrar" onClick={() => setShowResponse(false)} disabled={false} />
                            <ButtonNormal
                                caption="Copiar respuesta"
                                onClick={() => copyResponse(response)}
                                disabled={response.length === 0}
                            />
                            <button
                                className="px-3 py-1 text-white rounded-2xl bg-violetDark"
                                onClick={() => copyResponse(response)}
                                title="Copiar"
                            >
                                <ContentCopyIcon />
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {waitOverlay ? (
                <div
                    className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-slate-950/88 px-4 backdrop-blur-md"
                    role="alertdialog"
                    aria-busy="true"
                    aria-live="polite"
                >
                    <div className="max-w-lg rounded-2xl border border-white/15 bg-slate-900/95 px-8 py-8 shadow-2xl ring-1 ring-black/40">
                        <div
                            className="mx-auto mb-6 h-14 w-14 rounded-full border-4 border-violet-400/40 border-t-white shadow-lg animate-spin"
                            aria-hidden
                        />
                        <p
                            className="text-center text-xl font-bold tracking-tight text-white"
                            style={{ textShadow: '0 0 1px rgba(0,0,0,1), 0 2px 12px rgba(0,0,0,0.85)' }}
                        >
                            {waitOverlay.title}
                        </p>
                        <p className="mt-4 text-center text-sm leading-relaxed text-slate-200">
                            {waitOverlay.subtitle}
                        </p>
                        {loading && isGenerating && typeof consult?.response === 'string' && consult.response.length > 0 ? (
                            <p className="mt-5 rounded-xl border border-violet-400/30 bg-violet-950/90 px-4 py-2.5 text-center text-xs font-semibold text-violet-50 shadow-inner">
                                {consult.response.length.toLocaleString('es-AR')} caracteres recibidos…
                            </p>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <PersonalitySelector selected={personalidad} onChange={handlePersonalitySelector} />
        </div>
    )
}

export default CartaNatal
